import "./Home.css";

import { ThemeProvider, createTheme, } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { countryOptions, gicsIndOptions, ratingEnums } from "./constants";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Autocomplete from "@mui/material/Autocomplete";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useFlag } from "@unleash/proxy-client-react";
import { useAuthHeader } from "react-auth-kit";
import GaugeComponent from "react-gauge-component";
import Plot from "react-plotly.js";
import { useLocation } from "react-router-dom";
import { getCcrResults } from "./api/APIUtils";

function HelpPopup() {
  return (
    <div className="ccr-help-popup-container">
      <h1>About the ABE Credit Artificial Intelligence Tool (CAIT)</h1>
      <p>
        Our credit rating metric, rated on a scale of 1 to 1000, is a proprietary measure that provides insight into an issuer's creditworthiness, aiding in decision-making processes.
      </p>
      <p>
        This metric provides a comprehensive assessment of various financial and risk factors, creating a quantifiable and standardized measure, all in a single number and calculated in seconds. It includes an issuer's financial health, market standing, and future outlook, allowing for a precise comparative analysis across a broad range of issuers. The inputs are simplified here for easy use.
      </p>
      <h2>Usage</h2>
      <p>
        Modifying the three fields, country of origin, industry and gross product margin will calculate the expected credit rating. It will also show the relationship between our credit rating and a common industry metric. Both with the expected rating on the right, and a predictive likelihood of gaining that weighting below.
      </p>
    </div>
  );
}

function ProbabilityGraph({ creditProbabilityDistribution, plotHeight }) {
  const enabled = useFlag("probability_graph");

  if (enabled) {
    return (
      <Plot
        data={[
          {
            x: creditProbabilityDistribution.x,
            y: creditProbabilityDistribution.y,
            type: "bar",
          }
        ]}
        layout={{
          // title: "Probability Distribution of Predicted Credit Ratings",
          plot_bgcolor: "rgba(0, 0, 0, 0)",
          paper_bgcolor: "rgba(0, 0, 0, 0)",
          margin: { t: 10, r: 10, b: 50, l: 30 },
          yaxis: {
            tickformat: ".0%",
            fixedrange: true,
          },
          xaxis: {
            fixedrange: true,
          },
          height: plotHeight,
          transition: {
            duration: 1000,
            easing: "cubic-in-out",
            ordering: "traces first",
          },
        }}
        config={{
          displayModeBar: false,
        }}
        useResizeHandler={true}
        style={{ width: "100%", height: "100%" }}
      />
    );
  }
}

function Home() {
  const authHeader = useAuthHeader();
  const { state, search } = useLocation();

  const defaultSelectedGrossProfitMargin = 50;

  const defaultCreditScore = 500;
  const defaultCreditRating = "BB+";
  const defaultCreditProbabilityDistribution = { x: Object.values(ratingEnums).reverse(), y: Array(Object.values(ratingEnums).length).fill(0) };
  const gicsIndOptionsSorted = [...gicsIndOptions].sort((a, b) => a.label.localeCompare(b.label));

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedGrossProfitMargin, setSelectedGrossProfitMargin] = useState(defaultSelectedGrossProfitMargin);
  const [creditScore, setCreditScore] = useState(defaultCreditScore);
  const [creditRating, setCreditRating] = useState(defaultCreditRating);
  const [creditProbabilityDistribution, setCreditProbabilityDistribution] = useState(defaultCreditProbabilityDistribution);
  const [plotHeight, setPlotHeight] = useState(0);

  const [helpOpen, setHelpOpen] = useState(state?.showHelp || false);

  const updateCreditPrediction = async (event) => {
    const nonNullInputs = [selectedCountry, selectedIndustry, selectedGrossProfitMargin];
    if (nonNullInputs.some((input) => input === null)) {
      return;
    }

    // Disable submit button while waiting for results
    event.target.disabled = true;

    try {
      const countryId = selectedCountry.value;
      const industryId = selectedIndustry.value;
      const grossProfitMargin = selectedGrossProfitMargin / 100;

      // TODO: Handle errors (I don't think this is necessary, but something to note)
      const rating = await getCcrResults(countryId, industryId, grossProfitMargin, "get_prediction", authHeader());
      const score = await getCcrResults(countryId, industryId, grossProfitMargin, "get_weighted_average_rating", authHeader());
      const probabilities = await getCcrResults(countryId, industryId, grossProfitMargin, "get_rating_probabilities", authHeader());

      setCreditRating(rating.data.result);
      setCreditScore(Math.round(score.data.result));
      setCreditProbabilityDistribution({
        x: Object.values(probabilities.data.result.Rating).reverse(),
        y: Object.values(probabilities.data.result.Probability).reverse(),
      });
    } finally {
      // Re-enable submit button
      event.target.disabled = false;
    }

  }

  const getRatingColor = (creditScore) => {
    if (creditScore <= 200) return "#FF0000";
    else if (creditScore <= 400) return "#FFA500";
    else if (creditScore <= 600) return "#AAFF00";
    else return "#00FF00";
  }

  let theme = createTheme();
  theme.typography.h2 = {
    fontFamily: "Suisse Condensed",
    fontSize: "2rem",
    color: getRatingColor(creditScore),
    "@media (min-width:600px)": {
      fontSize: "2.5rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "3rem",
    },
  };

  useEffect(() => {
    let timeoutId;

    function resizePlot() {
      const screenHeight = window.innerHeight;
      const newHeight = parseInt((screenHeight / 1440) * 400);
      setPlotHeight(newHeight);
    }
    resizePlot(); // Initial resize on page load

    // Debounce resize event to prevent constant rerenders when the user is resizing the window (which can cause performance issues)
    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        resizePlot();
      }, 1000);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const CountryFlag = ({ code }) => (
    <img
      loading="lazy"
      width="20"
      src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`}
      alt=""
    />
  );

  const formatGaugeValue = (value) => {
    return value;
  }

  const handleHelpButton = () => {
    setHelpOpen(true);
  }

  const handleHelpClose = () => {
    setHelpOpen(false);
  }

  useEffect(() => {
    if (helpOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [helpOpen]);

  return (
    <div className="Home" data-testid="home-page-div">
      <div className="Home-outputs" data-testid="outputs-div">
        <div className="Home-outputs-header">
          <Tooltip title="Click for help">
            <IconButton className="Home-outputs-header-help-icon" aria-label="help" onClick={handleHelpButton}>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
          <Backdrop
            sx={{
              overflow: "auto",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1
            }}
            open={helpOpen}
            onClick={handleHelpClose}
          >
            <HelpPopup />
          </Backdrop>
          <div className="Home-outputs-credit-rating">
            <div>
              <GaugeComponent
                className="Home-outputs-credit-rating-gauge"
                type="semicircle"
                arc={{
                  // colorArray: ["#FF0000", "#00FF00"],
                  padding: 0.02,
                  subArcs:
                    [
                      // Make sure these limits match the limits in getRatingColor() otherwise the colors will be weird
                      { limit: 200, color: getRatingColor(200) },
                      { limit: 400, color: getRatingColor(400) },
                      { limit: 600, color: getRatingColor(600) },
                      { limit: 1000, color: getRatingColor(1000) },
                    ]
                }}
                labels={{
                  valueLabel: {
                    formatTextValue: formatGaugeValue,
                    matchColorWithArc: true,
                    style: {
                      fontSize: "50px",
                      // fill: "#fff",
                      textShadow: "",
                    }
                  },
                  tickLabels: {
                    hideMinMax: true,
                    style: {
                      fontSize: "10px",
                      fill: "#464A4F",
                      textShadow: "black 1px 1px 0px, black 0px 0px 2.5em, black 0px 0px 0.2em"
                    }
                  }
                }}
                pointer={{ type: "blob", animationDelay: 0 }}
                minValue={0}
                maxValue={1000}
                value={creditScore}
              />
            </div>
            <div>
              <ThemeProvider theme={theme}>
                {/* <h1 className="Home-outputs-credit-rating-header" style={{ color: getRatingColor() }}>{creditRating}</h1> */}
                <Typography variant="h2">{creditRating}</Typography>
              </ThemeProvider>
            </div>
          </div>
        </div>

        <ProbabilityGraph creditProbabilityDistribution={creditProbabilityDistribution} plotHeight={plotHeight} />
      </div>

      <div className="Home-inputs" data-testid="inputs-div">
        <div className="Home-inputs-autocompletes">
          <Autocomplete
            disablePortal
            id="combo-box-countries"
            options={countryOptions}
            getOptionLabel={(option) => option.label}
            onChange={(event, newValue) => setSelectedCountry(newValue)}
            renderOption={(props, option) => (
              <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                <CountryFlag code={option.code} />
                {option.label} ({option.code})
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedCountry && (
                    <CountryFlag code={selectedCountry.code} />
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            disablePortal
            id="combo-box-industries"
            options={gicsIndOptionsSorted}
            getOptionLabel={(option) => option.label}
            onChange={(event, newValue) => setSelectedIndustry(newValue)}
            renderInput={(params) => <TextField {...params} label="Industry" />}
          />
        </div>

        <Box sx={{ width: "100%" }}>
          <Typography gutterBottom>Gross Profit Margin</Typography>
          <Slider
            defaultValue={defaultSelectedGrossProfitMargin}
            aria-label="Gross Profit Margin"
            valueLabelDisplay="auto"
            onChange={(event, newValue) => setSelectedGrossProfitMargin(newValue)}
            valueLabelFormat={(value) => `${value}%`}
            min={0}
            max={100}
          />
        </Box>

        <button className="Home-inputs-submit-button" onClick={(event) => updateCreditPrediction(event)}>Submit</button>
      </div>
      <p className="Home-outputs-disclaimer-text">This model has been prepared by Australian Bond Exchange Pty Ltd ACN 605 038 935 AFSL 484453. It is a model, not a prediction. It estimates the credit risk associated with securities issued by an entity within a particular industry located in a particular jurisdiction. It does not take into account any product features, or extraneous or unknown factors which may arise over the period of an investment. The output of the model is subject to the assumptions used in the calculation and are subject to change. This model is not a recommendation to invest in a security issued by companies in a particular industry or in a particular jurisdiction. It does not take into account your financial needs, circumstances and objectives. Before investing in a fixed-interest product, you should consider whether it is appropriate for your circumstances and review the relevant terms and conditions. No representation or warranty is made as to the accuracy, completeness or reliability of any estimates, opinions, conclusions, or other information contained in this model.</p>
    </div >
  );
}

export default Home;